type CharacterLike = Record<string, unknown>;
type CharacterLookup = (id: string) => CharacterLike | null | undefined;

const REFERENCE = 'reference';
const COPY = 'copy';

/** Library bookkeeping — meaningful for a stored record, meaningless once embedded. */
const LIBRARY_FIELDS = [
    '_id',
    '_originalId',
    '_path',
    '_parent_id',
    '_collection',
    '_userid',
    '_campaign',
    '_lastModified',
];

/**
 * True when the token carries its own character data. Only reference tokens do
 * not; everything else is self-contained and is written through the token.
 */
export function ownsCharacter(token: CharacterLike | null | undefined): boolean {
    return !!token && token._tokenBehavior !== REFERENCE;
}

/**
 * The character behind a token: resolved for a reference, embedded for a copy, the
 * record itself for a legacy flat token. Every surface goes through this.
 */
export function tokenCharacter(
    token: CharacterLike | null | undefined,
    lookupCharacter?: CharacterLookup,
): CharacterLike | null {
    if (!token) return null;
    if (token._tokenBehavior === REFERENCE) {
        const id = token.characterId as string | undefined;
        return (id && lookupCharacter?.(id)) || token;
    }
    if (token._tokenBehavior === COPY) {
        return (token.character as CharacterLike) ?? token;
    }
    return token;
}

/** The token updated to carry `character`, wherever that kind of token keeps it. */
export function tokenWithCharacter(token: CharacterLike, character: CharacterLike): CharacterLike {
    return token._tokenBehavior === COPY ? { ...token, character } : { ...token, ...character };
}

/**
 * A detached clone with the library's identity removed. The embedded record is
 * data, not a library entry; dropping `_id` keeps character writes off the source.
 */
export function embedCharacter(character: CharacterLike): CharacterLike {
    const embedded = JSON.parse(JSON.stringify(character ?? {})) as CharacterLike;
    for (const field of LIBRARY_FIELDS) delete embedded[field];
    return embedded;
}

/**
 * The `{token, character}` pair the sheets components expect.
 *
 * WARNING: their `useTokenSource` treats `_tokenBehavior: 'copy'` as a promise that
 * sheet data lives in `token.tokenData`, and reads it before looking at
 * `character`. Our copies carry a character, so the marker must come off.
 */
export function libraryTokenProps(
    token: CharacterLike | null | undefined,
    lookupCharacter?: CharacterLookup,
): { token: CharacterLike; character?: CharacterLike } {
    if (!token) return { token: {} };
    const character = tokenCharacter(token, lookupCharacter);
    if (!character || character === token) return { token };

    const tokenForLibrary = { ...token };
    delete tokenForLibrary._tokenBehavior;
    return { token: tokenForLibrary, character };
}
